import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { PatientCheckInStatus, PatientStatusResponse } from '../types/agenda.types';
import {
  getPatientStatus,
  getAppointmentPractices,
  checkInAppointment,
} from '../services/AgendaService';
import { AppointmentPractice } from '../types/agenda.types';

import { PatientRecordDTO, defaultNewPatientRecordDTO } from '../../../interface/interface';
import PatientForm from '../../../components/PatientForm/PatientForm';
import { parseApiError } from '../types/error.types';
import './PatientCheckInModal.scss';
import { savePatient } from '../../../servicesHooks/DigitalRP/useDigitalRPService';
import PracticesEditor from './PracticesEditor';

interface PatientCheckInModalProps {
  appointmentId: number;
  visible: boolean;
  onHide: () => void;
  onCompleted: () => void;
  onError?: (message: string) => void;
  /**
   * Después de registrar la llegada se sigue con el cobro del turno, como Vaadin. Con "Registrar y cobrar",
   * {@code revertArrivalOnCancel} pide revertir la llegada si al final no se registra la orden.
   */
  onBillingRequested?: (appointmentId: number, revertArrivalOnCancel: boolean) => void;
  /** When true, opens the form for editing without registering arrival */
  editMode?: boolean;
}

export default function PatientCheckInModal({
  appointmentId,
  visible,
  onHide,
  onCompleted,
  onError,
  onBillingRequested,
  editMode = false,
}: PatientCheckInModalProps) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [onlyImages, setOnlyImages] = useState(false);
  const [statusId, setStatusId] = useState<number>(PatientCheckInStatus.COMPLETE);
  const [initialPatient, setInitialPatient] = useState<PatientRecordDTO | null>(null);
  const [practices, setPractices] = useState<AppointmentPractice[]>([]);
  const [formValid, setFormValid] = useState(false);
  const currentPatientRef = useRef<PatientRecordDTO | null>(null);

  const handleArrivalSuccess = useCallback((withBilling: boolean) => {
    onCompleted();
    if (onBillingRequested) {
      onBillingRequested(appointmentId, withBilling);
      return;
    }
    onHide();
  }, [appointmentId, onBillingRequested, onCompleted, onHide]);

  const handleClose = useCallback(() => {
    onHide();
  }, [onHide]);

  // Load status on open
  useEffect(() => {
    if (!visible || !appointmentId) return;
    setOnlyImages(false);

    setLoading(true);
    Promise.all([getPatientStatus(appointmentId), getAppointmentPractices(appointmentId)]).then(([res, savedPractices]: [PatientStatusResponse, AppointmentPractice[]]) => {
      setStatusId(res.statusId);
      setOnlyImages(Boolean(res.onlyImages));
      setPractices(savedPractices ?? []);

      const data = res.patientData;
      const patient: PatientRecordDTO = { ...defaultNewPatientRecordDTO };

      if (res.statusId === PatientCheckInStatus.INCOMPLETE || res.statusId === PatientCheckInStatus.COMPLETE) {
        patient.medereentity = data.medereentity ?? -1;
        patient.firstname = data.firstname ?? '';
        patient.lastname = data.lastname ?? '';
        patient.document = data.document ?? '';
        patient.gender = data.gender ?? '';
        patient.birthdate = data.birthdate ?? undefined;
        patient.mobilephone = data.mobilephone ?? '';
        patient.email = data.email ?? '';
        patient.insurances = data.insurances ?? [];
      } else if (res.statusId === PatientCheckInStatus.UNIDENTIFIED) {
        // Pre-fill from provisional contact
        const refParts = (data.referenceName ?? '').split(' ');
        patient.firstname = refParts[0] ?? '';
        patient.lastname = refParts.slice(1).join(' ') ?? '';
        patient.document = data.document ?? '';
        patient.mobilephone = data.phone ?? '';

        // Pre-fill coverage from provisional contact if present
        if (data.healthEntityPlanId) {
          patient.insurances = [{
            healthpatientcoverage: 0,
            benefitnumber: data.benefitNumber ?? '',
            coveragestart: undefined,
            coverageend: undefined,
            primarycoverage: true,
            voluntary: true,
            patient: 0,
            description: '',
            healthentityplan: {
              healthentityplan: data.healthEntityPlanId,
              name: data.healthEntityPlanName ?? '',
              healthentity: {
                healthentity: data.healthEntityId ?? 0,
                commercialname: data.healthEntityName ?? '',
                recipevalidatorid: 4,
              },
            },
          }];
        }
      }

      setInitialPatient(patient);
      currentPatientRef.current = patient;
      setLoading(false);
    }).catch((err) => {
      onError?.(parseApiError(err, 'No se pudieron cargar los datos del paciente. Intentá de nuevo.'));
      setLoading(false);
      onHide();
    });
  }, [visible, appointmentId]);

  const handlePatientChange = useCallback((patient: PatientRecordDTO) => {
    currentPatientRef.current = patient;
  }, []);


  const handleConfirm = useCallback(async (withBilling: boolean) => {
    const patient = currentPatientRef.current;
    if (!patient) return;

    setSubmitting(true);
    try {
      // Save patient (creates or updates)
      const savedPatient = await savePatient(patient, 1, 0);
      const savedPatientId = savedPatient?.patient ?? savedPatient?.medereentity;

      if (!savedPatientId) {
        console.error('Could not resolve saved patient ID');
        return;
      }

      if (!editMode) {
        // Persist patient, appointment practices and arrival as one backend operation.
        await checkInAppointment(appointmentId, savedPatientId, practices);
      }

      if (editMode) {
        onCompleted();
        onHide();
      } else {
        handleArrivalSuccess(withBilling);
      }
    } catch (err) {
      onError?.(parseApiError(err, editMode
        ? 'No se pudieron guardar los datos del paciente. Intentá de nuevo.'
        : 'No se pudo registrar la llegada. Intentá de nuevo.'));
      onHide();
    } finally {
      setSubmitting(false);
    }
  }, [appointmentId, editMode, onCompleted, onHide, handleArrivalSuccess, practices]);

  const getHeaderTitle = () => {
    if (editMode) return 'Editar datos del paciente';
    if (statusId === PatientCheckInStatus.UNIDENTIFIED) return 'Nuevo paciente';
    return 'Registrar llegada';
  };

  // Como la llegada de Vaadin: "Registrar llegada" (no va en agendas solo de imágenes) y "Registrar y cobrar"
  const formFooter = (
    <>
      <Button type="button" label="Cancelar" className="buttonMain2" outlined onClick={handleClose} />
      {editMode ? (
        <Button type="button" label="Guardar cambios" className="buttonMain" onClick={() => handleConfirm(false)}
          disabled={!formValid || submitting} loading={submitting} />
      ) : (
        <>
          {!onlyImages && (
            <Button type="button" label="Registrar llegada" className="buttonMain2" outlined onClick={() => handleConfirm(false)}
              disabled={!formValid || submitting} />
          )}
          <Button type="button" label="Registrar y cobrar" icon="pi pi-dollar" className="buttonMain" onClick={() => handleConfirm(true)}
            disabled={!formValid || submitting} loading={submitting} />
        </>
      )}
    </>
  );

  return (
    <>
      <Dialog
      visible={visible && !loading}
      onHide={handleClose}
      header={getHeaderTitle()}
      footer={formFooter}
      modal
      className="nv-scope nv-dialog"
      style={{ width: '900px' }}
      closable
      draggable={false}
    >
      {initialPatient !== null && (
        <PatientForm
          initialPatient={initialPatient}
          onPatientChange={handlePatientChange}
          onValidityChange={setFormValid}
          hideTitle
          hideObservations
        />
      )}
      {!editMode && (
        <section className="nv-section checkin-practices" aria-labelledby="checkin-practices-title">
          <h3 className="nv-section-title" id="checkin-practices-title">Prácticas del turno</h3>
          <PracticesEditor practices={practices} onChange={setPractices} onError={onError} />
        </section>
      )}
      </Dialog>
    </>
  );
}
